import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import {
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";

import { RefreshRounded, ControlPoint } from "@mui/icons-material";

import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { format } from "date-fns";
import dayjs from "dayjs";
import "react-notifications-component/dist/theme.css";

import * as actions from "../../redux/actions/index";
import PageLoading from "../../components/PageLoading/PageLoading.component";
import PulseLoading from "../../components/PulseLoading/PulseLoading.component";
import AddStoreModal from "../../components/AddStoreModal/AddStoreModal.component";
import ImageRotate from "../../components/ImageRotate/ImageRotate";
import { Fields } from "../../components/Fields/Fields.component";
import { CustomTooltip } from "../../components/CustomTooltip/CustomTooltip.component";
import { CustomDialogMessage } from "../../components/CustomDialogMessage/CustomDialogMessage.component";
import { CustomSelect } from "../../components/CustomSelect/CustomSelect.component";
import { CustomSnackbar } from "../../components/CustomSnackbar/CustomSnackbar.component";
import {
  validateMaxUnitPrice,
  validateMinUnitPrice,
} from "../../utils/validatePrice";
import { validateQty } from "../../utils/validateQty";
import {
  generateStoresOptions,
  generateBranchesOptions,
  generateProductsOptions,
  generateSkusOptions,
  generateRejectRemarksOptions,
  statusOption,
} from "../../utils/optionsUtils";

import { useStyles } from "./useStyles";
import TransactionViewAction from "./TransactionViewAction";
import TransactionViewModal from "./TransactionViewModal";
import TransactionViewProducts from "./TransactionViewProducts";

const TransactionView = () => {
  let { id } = useParams();
  const { control, setValue, handleSubmit, getValues } = useForm();
  const dispatch = useDispatch();
  const classes = useStyles();
  const auth = useSelector((state) => state.cmsAuthenticator.cmsAuth);
  const transaction = useSelector((state) => state.cmsTransactions.transaction);
  const products = useSelector((state) => state.cmsProducts.products);
  const skus = useSelector((state) => state.cmsProducts.sku);
  const stores = useSelector((state) => state.cmsTransactions.stores);
  const branches = useSelector((state) => state.cmsTransactions.branches);
  const actionSuccessMessage = useSelector(
    (state) => state.cmsTransactions.actionMessageSuccess
  );
  const actionErrorMessage = useSelector(
    (state) => state.cmsTransactions.actionMessageFailed
  );
  const rejectRemarks = useSelector(
    (state) => state.cmsTransactions.rejectRemarks
  );
  const loading = useSelector(
    (state) => state.cmsTransactions.fetchTransactionLoading
  );
  const actionLoading = useSelector(
    (state) => state.cmsTransactions.actionLoading
  );
  const storesLoading = useSelector(
    (state) => state.cmsTransactions.storesLoading
  );
  const branchesLoading = useSelector(
    (state) => state.cmsTransactions.branchesLoading
  );
  const productsLoading = useSelector(
    (state) => state.cmsTransactions.productsLoading
  );
  const [receiptDate, setReceiptDate] = useState(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [discardModal, setDiscardModal] = useState(false);
  const [discardStatusModal, setDiscardStatusModal] = useState(false);
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState(null);
  const [invalidTypes, setInvalidType] = useState(null);
  const [receiptRejection, setReceiptRejection] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeModal, setStoreModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState(null);
  const [dialogMsg, setDialogMsg] = useState("");
  const maxBigQuantityRef = useRef(0);
  const minUnitPriceRef = useRef(0);
  const maxUnitPriceRef = useRef(0);
  const minProductPriceRef = useRef(0); //known min product price
  const maxProductPriceRef = useRef(0); //known max product price
  const [isSku, setIsSku] = useState(true);
  const [isClearable] = useState(true);

  const role = auth.role.toLowerCase().replace("_", "");

  //REF FOR DIALOG
  const createSetRefFunction = (ref) => (value) => {
    ref.current = value;
  };
  const setMaxBigQuantity = createSetRefFunction(maxBigQuantityRef);
  const setMinUnitPrice = createSetRefFunction(minUnitPriceRef);
  const setMaxUnitPrice = createSetRefFunction(maxUnitPriceRef);
  const setMinProductPrice = createSetRefFunction(minProductPriceRef);
  const setMaxProductPrice = createSetRefFunction(maxProductPriceRef);

  const storesOption = generateStoresOptions(stores);
  const branchesOption = generateBranchesOptions(branches);
  const productsOption = generateProductsOptions(products);
  const skusOption = generateSkusOptions(skus);
  const rejectremarksOption = generateRejectRemarksOptions(rejectRemarks);

  // USEEFFECT
  useEffect(() => {
    dispatch(actions.onFetchTransaction(id, role));
  }, [dispatch, id, role]);

  useEffect(() => {
    dispatch(actions.onFetchProducts());
    dispatch(actions.onFetchSku());
    dispatch(actions.onFetchRejectRemarks());
    dispatch(actions.onFetchStores());
  }, [dispatch]);

  useEffect(() => {
    if (transaction && transaction.status) {
      setStatus(transaction.status);
    }

    if (transaction && transaction.store_id) {
      dispatch(actions.onFetchBranches(transaction.store_id));
    }

    if (transaction && transaction.store_id == null) {
      setSelectedStore({
        name: null,
        id: null,
      });
    }
    if (transaction && transaction.branch_id === null) {
      setSelectedBranch({
        address: "",
        brand_id: null,
        city: "",
        id: null,
        name: "",
        province: "",
        region: "",
        store_id: null,
      });
    }
  }, [dispatch, transaction]);

  useEffect(() => {
    if (transaction.receipt_number !== null) {
      setValue("receiptNumber", transaction.receipt_number);
    }
    if (transaction) {
      setValue("receiptTotal", transaction.receipt_total);
    }
    if (transaction && transaction.brand && transaction.brand.name) {
      setValue("brand", transaction.brand.name);
    }
    const today = new Date();
    if (transaction && transaction.receipt_date !== "null") {
      setReceiptDate(transaction.receipt_date);
    } else {
      const formattedToday = format(today, "yyyy-MM-dd HH:mm");
      setReceiptDate(formattedToday);
    }
  }, [transaction, setValue]);

  // Set invalid type based on reject remarks and transaction's invalid type ID
  useEffect(() => {
    if (rejectRemarks?.length && transaction?.invalid_type_id) {
      const remarks = rejectRemarks.find(
        (remark) => remark.code === transaction.invalid_type_id
      );
      const initialRemarks = {
        label: remarks.name,
        value: remarks.code,
      };
      setInvalidType(initialRemarks);
    }
  }, [rejectRemarks, transaction]);

  // Update selected store based on transaction's store ID, if both stores and transaction are available
  useEffect(() => {
    if (stores?.length && transaction?.store_id) {
      const store = stores.find((store) => store.id === transaction.store_id);

      const initialStore = {
        label: store ? store.name : null,
        value: store ? store.id : null,
        ...store,
      };
      setSelectedStore(initialStore);
    }
  }, [stores, transaction]);

  // Update selected branch based on transaction's branch ID, if both branches and transaction are available
  useEffect(() => {
    if (branches?.length && transaction?.branch_id) {
      const branch = branches.find(
        (branch) => branch.id === transaction.branch_id
      );
      const initialBranch = {
        label: branch ? branch.name : null,
        value: branch ? branch.id : null,
        ...branch,
      };
      setSelectedBranch(initialBranch);
    }
  }, [branches, transaction]);

  // Update receipt rejection based on available reject remarks
  useEffect(() => {
    if (rejectRemarks?.length > 0) {
      const rejectionRemarks = rejectRemarks.filter(
        (remark) => remark.display !== false
      );
      const initialRejectionRemarks = rejectionRemarks.map((remarks) => ({
        label: remarks.name,
        value: remarks.code,
        ...remarks,
      }));
      if (initialRejectionRemarks.length > 0) {
        setReceiptRejection(null);
      } else {
        setReceiptRejection(initialRejectionRemarks);
      }
    }
  }, [rejectRemarks]);

  // Update selected products and SKUs based on transaction's receipt items
  useEffect(() => {
    if (transaction?.receipt_items) {
      const receiptItemsWithFlag = transaction.receipt_items.map((item) => ({
        // Add `isExisting: true` to indicate these items are part of the existing transaction
        ...item,
        isExisting: true, // This flag shows that the item is from an existing transaction
      }));

      // PRODUCTS
      const initialProducts = receiptItemsWithFlag.map((item) =>
        products.find((product) => product.id === item.product_id)
      );
      const productValues = initialProducts.map((product) => ({
        label: product ? product.name : "",
        value: product ? product.id : null,
        ...product,
      }));
      setSelectedProducts(productValues);

      // SKU
      const initialSkus = receiptItemsWithFlag.map((item) =>
        skus.find((sku) => sku.id === item.sku_id)
      );
      const skuValues = initialSkus.map((sku) => ({
        label: sku ? sku.product_code : "",
        value: sku ? sku.id : null,
        item_price: sku ? sku.price : null,
      }));
      setSelectedSkus(skuValues);
      setItems(receiptItemsWithFlag);
    }
  }, [transaction, products, skus]);

  // SKU TOGGLE
  const handleChangeIsSku = (event) => {
    setIsSku(event.target.checked);
  };

  // HANDLE STORE SELECTION CHANGE
  const handleStoreChange = (selectedOption) => {
    const storeId = selectedOption ? selectedOption.id : null;
    setSelectedStore({
      label: selectedOption ? selectedOption.name : null,
      value: selectedOption ? selectedOption.id : null,
      ...selectedOption,
    });
    dispatch(actions.onFetchBranches(storeId));
  };

  // HANDLE STATUS SELECTION CHANGE
  const handleStatusChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      setStatus(selectedOption.value);
    }
  };

  // HANDLE REJECT SELECTION CHANGE
  const handleRejectChange = (selectedOption) => {
    setReceiptRejection(selectedOption);
  };

  // HANDLE BRANCH SELECTION CHANGE
  const handleBranchChange = (selectedOption) => {
    setSelectedBranch({
      label: selectedOption ? selectedOption.name : null,
      value: selectedOption ? selectedOption.id : null,
      ...selectedOption,
    });
  };

  // HANDLE ADDING NEW PRODUCTS
  const handleProductSelect = (idx, selectedOption) => {
    const updatedProducts = [...selectedProducts];
    updatedProducts[idx] = selectedOption;
    setSelectedProducts(updatedProducts);

    // Find the matched SKU for the selected product
    if (selectedOption) {
      const matchedSku = skus.find((sku) =>
        products.find(
          (product) =>
            sku.product_id === product.id && product.id === selectedOption.value
        )
      );

      const updatedItems = [...items];
      updatedItems[idx] = {
        ...updatedItems[idx],
        item_name: selectedOption ? selectedOption.label : "",
        product_id: selectedOption ? selectedOption.value : "",
        sku_id: selectedOption ? matchedSku.id : "",
        item_price: !updatedItems[idx].isExisting
          ? selectedOption.price
          : updatedItems[idx].item_price,
        item_code: selectedOption ? selectedOption.code : "",
      };
      setItems(updatedItems);
    } else {
      const updatedItems = [...items];
      updatedItems[idx] = {
        ...updatedItems[idx],
        item_name: "",
        item_code: "",
        product_id: "",
        item_price: updatedItems[idx].isExisting,
      };
      setItems(updatedItems);
    }
  };

  // HANDLE ADDING NEW SKUS
  const handleSkuSelect = (idx, selectedOption) => {
    const updatedSkus = [...selectedSkus];
    updatedSkus[idx] = selectedOption;
    setSelectedSkus(updatedSkus);

    if (selectedOption) {
      const matchedSku = skus.find((sku) =>
        products.find(
          (product) =>
            sku.product_id === product.id &&
            product.id === selectedOption.product_id
        )
      );

      const updatedItems = [...items];
      updatedItems[idx] = {
        ...updatedItems[idx],
        item_name: selectedOption.label,
        item_code: selectedOption.label,
        product_id: selectedOption.product_id,
        item_price: !updatedItems[idx].isExisting // Update price only if the item is not from an existing transaction
          ? selectedOption.price
          : updatedItems[idx].item_price,
        sku_id: matchedSku ? matchedSku.id : "",
      };
      setItems(updatedItems);
    } else {
      const updatedItems = [...items];
      updatedItems[idx] = {
        ...updatedItems[idx],
        item_name: "",
        item_code: "",
        product_id: "",
        item_price: updatedItems[idx].item_price,
        sku_id: "",
      };
      setItems(updatedItems);
    }
  };

  // ADD/EDIT/REMOVE PRODUCT
  const onAddItem = () => {
    // Add a new item with `isExisting: false` to indicate it’s a new item
    const newItem = {
      product_id: 0,
      sku_id: 0,
      item_name: null,
      item_code: null,
      item_price: 1,
      quantity: 1,
      isExisting: false, // This flag indicates that the item is newly added
    };
    setItems(items ? [...items, newItem] : [newItem]);
  };

  const onRemoveItem = (index) => {
    const list = [...items];
    list.splice(index, 1);
    setItems([...list]);

    if (isSku) {
      const newSelectedSkus = [...selectedSkus];
      newSelectedSkus.splice(index, 1);
      setSelectedSkus(newSelectedSkus);
    } else {
      const newSelectedProducts = [...selectedProducts];
      newSelectedProducts.splice(index, 1);
      setSelectedProducts(newSelectedProducts);
    }
  };

  const onQuantityChangedHandler = (event, index) => {
    const list = [...items];
    list[index]["quantity"] = parseInt(event.target.value);
    setItems([...list]);
  };

  const onItemPriceChangedHandler = (event, index) => {
    const list = [...items];
    list[index]["item_price"] = parseFloat(event.target.value);
    setItems([...list]);
  };

  // ON PREAPPROVE
  const onProcessApprove = (data) => {
    const checkQty = validateQty(items, setMaxBigQuantity);
    const checkMaxPrice = validateMaxUnitPrice(
      items,
      selectedSkus,
      selectedProducts,
      isSku,
      setMaxUnitPrice,
      setMaxProductPrice
    );
    const checkMinPrice = validateMinUnitPrice(
      isSku,
      items,
      selectedSkus,
      selectedProducts,
      setMinUnitPrice,
      setMinProductPrice
    );
    CustomDialogMessage(
      checkQty,
      checkMaxPrice,
      checkMinPrice,
      maxBigQuantityRef.current,
      maxUnitPriceRef.current,
      maxProductPriceRef.current,
      minUnitPriceRef.current,
      minProductPriceRef.current,
      setDialogMsg
    );
    if (!checkQty && !checkMaxPrice && !checkMinPrice) onPreapprove(data);
  };

  const onPreapprove = (data) => {
    handleSubmit(onApproveTransaction(data));
  };

  // PROCEED BIG QTY
  const proceedBigQuantity = () => {
    const data = getValues();
    handleSubmit(onApproveTransaction(data));
    setDialogMsg("");
  };

  const doubleCheck = () => {
    setDialogMsg("");
  };

  const onApproveTransaction = (data) => {
    const storeId = selectedStore.id == null ? null : selectedStore.id;
    const branchId = selectedBranch.id == null ? null : selectedBranch.id;
    const receiptdateData =
      transaction.receipt_date == null
        ? dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")
        : dayjs(receiptDate).format("YYYY-MM-DD HH:mm:ss");

    const sanitizedItems = items.map(({ isExisting, ...item }) => item); // Create a sanitized copy of items without the `isExisting` property

    const receiptData = JSON.stringify({
      store_id: storeId,
      branch_id: branchId,
      receipt_merchant: `${selectedStore.name}`,
      receipt_branch: `${selectedBranch.name}`,
      receipt_branch_city: `${selectedBranch.city}`,
      receipt_branch_province: `${selectedBranch.province}`,
      receipt_branch_region: `${selectedBranch.region}`,
      receipt_merchant_address: `${selectedBranch.address}`,
      receipt_number: data.receiptNumber,
      receipt_items: sanitizedItems,
      receipt_total: parseFloat(data.receiptTotal),
      receipt_date: receiptdateData,
      account_id: auth.id,
    });
    dispatch(actions.onApproveReceipt(id, receiptData));
  };

  const onRejectTransaction = async (data) => {
    const storeId = selectedStore.id == null ? null : selectedStore.id;
    const branchId = selectedBranch.id == null ? null : selectedBranch.id;
    const invalidType =
      receiptRejection == null ? null : parseInt(receiptRejection.code);
    const receiptdateData =
      transaction.receipt_date == null
        ? dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")
        : dayjs(receiptDate).format("YYYY-MM-DD HH:mm:ss");

    const sanitizedItems = items.map(({ isExisting, ...item }) => item); // Create a sanitized copy of items without the `isExisting` property

    const receiptData = JSON.stringify({
      store_id: storeId,
      branch_id: branchId,
      receipt_merchant: `${selectedStore.name}`,
      receipt_branch: `${selectedBranch.name}`,
      receipt_branch_city: `${selectedBranch.city}`,
      receipt_branch_province: `${selectedBranch.province}`,
      receipt_branch_region: `${selectedBranch.region}`,
      receipt_merchant_address: `${selectedBranch.address}`,
      receipt_number: data.receiptNumber,
      receipt_items: sanitizedItems,
      invalid_type_id: invalidType,
      receipt_total: parseFloat(data.receiptTotal),
      receipt_date: receiptdateData,
      account_id: auth.id,
    });
    dispatch(actions.onRejectReceipt(id, receiptData));
    setRejectModal(false);
  };

  const onEditTransaction = async (data) => {
    const storeId = selectedStore.id == null ? null : selectedStore.id;
    const branchId = selectedBranch.id == null ? null : selectedBranch.id;
    const receiptdateData =
      transaction.receipt_date == null
        ? dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")
        : dayjs(receiptDate).format("YYYY-MM-DD HH:mm:ss");

    const sanitizedItems = items.map(({ isExisting, ...item }) => item); // Create a sanitized copy of items without the `isExisting` property

    const receiptData = JSON.stringify({
      store_id: storeId,
      branch_id: branchId,
      receipt_merchant: `${selectedStore.name}`,
      receipt_branch: `${selectedBranch.name}`,
      receipt_branch_city: `${selectedBranch.city}`,
      receipt_branch_province: `${selectedBranch.province}`,
      receipt_branch_region: `${selectedBranch.region}`,
      receipt_merchant_address: `${selectedBranch.address}`,
      receipt_number: data.receiptNumber,
      receipt_items: sanitizedItems,
      receipt_total: parseFloat(data.receiptTotal),
      receipt_date: receiptdateData,
      account_id: auth.id,
      remarks: "",
      status: status,
    });
    if (status === "DISCARDED") {
      setDiscardStatusModal(true);
    } else {
      dispatch(actions.onEditTransaction(id, receiptData));
    }
  };

  const onDiscardReceipt = async () => {
    setDiscardModal(false);
    const receiptData = JSON.stringify({
      account_id: auth.id,
    });
    dispatch(actions.onDiscardTransaction(id, receiptData));
  };

  const onStatusDiscard = async (data) => {
    setDiscardStatusModal(false);
    const storeId = selectedStore.id == null ? null : selectedStore.id;
    const branchId = selectedBranch.id == null ? null : selectedBranch.id;
    const receiptdateData =
      transaction.receipt_date == null
        ? dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")
        : dayjs(receiptDate).format("YYYY-MM-DD HH:mm:ss");

    const sanitizedItems = items.map(({ isExisting, ...item }) => item); // Create a sanitized copy of items without the `isExisting` property

    const receiptData = JSON.stringify({
      store_id: storeId,
      branch_id: branchId,
      receipt_merchant: `${selectedStore.name}`,
      receipt_branch: `${selectedBranch.name}`,
      receipt_branch_city: `${selectedBranch.city}`,
      receipt_branch_province: `${selectedBranch.province}`,
      receipt_branch_region: `${selectedBranch.region}`,
      receipt_merchant_address: `${selectedBranch.address}`,
      receipt_number: data.receiptNumber,
      receipt_items: sanitizedItems,
      receipt_total: parseFloat(data.receiptTotal),
      receipt_date: receiptdateData,
      account_id: auth.id,
      remarks: "",
      status: status,
    });
    dispatch(actions.onEditTransaction(id, receiptData));
  };

  const onResendCouponHandler = () => {
    const data = JSON.stringify({
      type: "conclude",
      param: {
        transaction_id: Number(id),
      },
    });
    dispatch(actions.onResendCoupon(data));
  };

  const onVerifyDuplicate = (data) => {
    const storeId = selectedStore.id == null ? null : selectedStore.id;
    const receiptdateData =
      transaction.receipt_date == null
        ? dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")
        : dayjs(receiptDate).format("YYYY-MM-DD HH:mm:ss");
    const receiptData = JSON.stringify({
      receipt_number: data.receiptNumber,
      receipt_date: receiptdateData,
      store_id: storeId,
    });
    dispatch(actions.onFraudDetect(receiptData));
  };

  const viewContent = (
    <div className="TransactionView wrapper">
      <PageLoading loading={actionLoading} />
      <div className="TransactionView__container">
        <div className="TransactionView__imgContainer">
          <ImageRotate
            key={transaction.image_url}
            src={transaction.image_url}
            style={{ width: "100%" }}
          />
        </div>

        <form className="TransactionView__form">
          <div className="TransactionView__form--container">
            <Grid container spacing={2} style={{ alignItems: "end" }}>
              <Grid item xs={12} sm={12} md={4.5} lg={4.5}>
                <Fields
                  control={control}
                  name="receiptNumber"
                  inputLlabel="Receipt #"
                  placeholder="Receipt #"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={1.5} lg={1.5}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(onVerifyDuplicate)}
                >
                  VERIFY
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Fields
                  control={control}
                  name="receiptTotal"
                  inputLlabel="Total Amount"
                  placeholder="Total Amount"
                  type="number"
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6}>
                <InputLabel htmlFor="receipt-date">
                  Receipt Date and Time
                </InputLabel>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      fullWidth
                      value={
                        receiptDate ? dayjs(receiptDate) : dayjs(new Date())
                      }
                      onChange={(newValue) => setReceiptDate(newValue)}
                      minDate={dayjs("2023-12-15")}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {transaction && transaction.status !== "PENDING" && (
                  <>
                    <InputLabel htmlFor="transaction-status">Status</InputLabel>
                    <CustomSelect
                      value={{ value: status, label: status }}
                      options={statusOption}
                      isClearable={false}
                      onChange={handleStatusChange}
                    />
                  </>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              style={{ alignItems: "start", paddingTop: "1rem" }}
            >
              <Grid item xs={12} sm={12} md={12} lg={6}>
                <InputLabel htmlFor="store-branch">Store</InputLabel>
                {storesLoading ? (
                  <PulseLoading height="30px" />
                ) : (
                  <FormControl fullWidth>
                    <CustomSelect
                      value={selectedStore}
                      options={storesOption}
                      isClearable={isClearable}
                      onChange={(selectedOption) =>
                        handleStoreChange(selectedOption)
                      }
                    />
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={4}>
                <InputLabel htmlFor="store-branch">Branch</InputLabel>
                {branchesLoading ? (
                  <PulseLoading height="30px" />
                ) : (
                  <FormControl fullWidth className="branch">
                    <CustomSelect
                      value={selectedBranch}
                      options={branchesOption}
                      isClearable={isClearable}
                      onChange={(selectedOption) =>
                        handleBranchChange(selectedOption)
                      }
                    />
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={2} sm={2} md={2} lg={0.5}>
                <br />
                <IconButton
                  fullWidth
                  onClick={() =>
                    dispatch(actions.onFetchBranches(transaction.branch_id))
                  }
                >
                  <RefreshRounded />
                </IconButton>
              </Grid>

              {auth.role !== "SUPER_ADMIN" ? (
                !selectedStore ||
                (selectedStore.name !== null &&
                  selectedStore.is_official !== true && (
                    <Grid item xs={2} sm={2} md={2} lg={1}>
                      <br />
                      <FormControl>
                        <CustomTooltip
                          label={"Add Branch"}
                          icon={
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              style={{ fontSize: "1.2rem" }}
                              onClick={() => setStoreModal(true)}
                            >
                              <ControlPoint />
                            </Button>
                          }
                        />
                      </FormControl>
                    </Grid>
                  ))
              ) : (
                <Grid item xs={2} sm={2} md={2} lg={1}>
                  <br />
                  <FormControl>
                    <CustomTooltip
                      label={"Add Branch"}
                      icon={
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          style={{ fontSize: "1.2rem" }}
                          onClick={() => setStoreModal(true)}
                        >
                          <ControlPoint />
                        </Button>
                      }
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={12} md={6} lg={6} pb="2rem">
                {transaction && transaction.status === "INVALID" && (
                  <>
                    <InputLabel htmlFor="transaction-status">
                      Invalid Remark
                    </InputLabel>
                    <CustomSelect value={invalidTypes} />
                  </>
                )}
              </Grid>
            </Grid>
          </div>
          <Grid
            container
            spacing={10}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Grid item xs={2} sm={2} md={5} lg={1}>
              <h3 className="heading-tertiary">Products:</h3>
            </Grid>
            <Grid item xs={8} sm={2} md={5} lg={5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSku}
                    onChange={handleChangeIsSku}
                    name="sku"
                  />
                }
                label="Show SKUs"
              />
            </Grid>
          </Grid>

          <TransactionViewProducts
            classes={classes}
            isSku={isSku}
            items={items}
            productsLoading={productsLoading}
            selectedSkus={selectedSkus}
            skusOption={skusOption}
            handleSkuSelect={handleSkuSelect}
            selectedProducts={selectedProducts}
            productsOption={productsOption}
            handleProductSelect={handleProductSelect}
            onQuantityChangedHandler={onQuantityChangedHandler}
            onItemPriceChangedHandler={onItemPriceChangedHandler}
            onRemoveItem={onRemoveItem}
            dialogMsg={dialogMsg}
            doubleCheck={doubleCheck}
            proceedBigQuantity={proceedBigQuantity}
            onAddItem={onAddItem}
          />
        </form>
      </div>
      <TransactionViewAction
        transaction={transaction}
        auth={auth}
        handleSubmit={handleSubmit}
        onEditTransaction={onEditTransaction}
        onResendCouponHandler={onResendCouponHandler}
        setRejectModal={setRejectModal}
        onProcessApprove={onProcessApprove}
        setDiscardModal={setDiscardModal}
      />
      <TransactionViewModal
        rejectModal={rejectModal}
        receiptRejection={receiptRejection}
        rejectremarksOption={rejectremarksOption}
        handleRejectChange={handleRejectChange}
        setRejectModal={setRejectModal}
        onRejectTransaction={onRejectTransaction}
        discardModal={discardModal}
        discardStatusModal={discardStatusModal}
        setDiscardModal={setDiscardModal}
        setDiscardStatusModal={setDiscardStatusModal}
        onDiscardReceipt={onDiscardReceipt}
        onStatusDiscard={onStatusDiscard}
        handleSubmit={handleSubmit}
      />
    </div>
  );

  const restrict = auth.role !== "SUPER_ADMIN" &&
    auth.role !== "AGENT" &&
    auth.role !== "AUDITOR" && <Navigate to="/dashboard" />;
  return (
    <>
      {restrict}
      {loading ? <PageLoading loading={loading} /> : viewContent}
      <CustomSnackbar message={actionErrorMessage} severity="error" />
      <CustomSnackbar message={actionSuccessMessage} severity="success" />

      <AddStoreModal
        open={storeModal}
        close={() => setStoreModal(false)}
        transaction={transaction}
        selectedStore={selectedStore}
      />
    </>
  );
};

export default TransactionView;
